// UI Components and Rendering

import { Config, CatalogConfig, ENABLED_SORT_OPTIONS, CONTENT_TYPES } from './types';
import { toggleSection } from './utils';
import { debugConsole } from './debug';
import { CatalogIDStrings, CatalogIdEnum, CatalogId } from './constants';
import { CatalogPreview } from './preview';
import { CatalogDragDropManager } from './catalog-drag-drop';

// Store preview instances
const catalogPreviews = new Map<string, CatalogPreview>();

// Store drag manager instance
let dragDropManager: CatalogDragDropManager | null = null;

/**
 * Render catalog list dynamically based on groupByType setting
 */
export function renderCatalogList(config: Config): void {
  const catalogList = document.getElementById('catalogList');
  if (!catalogList) return;

  debugConsole.log(`Rendering catalog list (groupByType: ${config.groupByType}, catalogs: ${config.catalogs.length})`);

  // Log each catalog for debugging
  config.catalogs.forEach((catalog, index) => {
    debugConsole.log(`Catalog ${index}: ${catalog.name} (${catalog.id}) - Continue Watching: ${catalog.isContinueWatching || false}`);
  });

  catalogList.innerHTML = '';

  config.catalogs.forEach((catalog, index) => {
    const catalogItem = createCatalogItem(catalog, index, config.groupByType);
    catalogList.appendChild(catalogItem);

    // Apply disabled state if catalog is disabled
    if (!catalog.enabled) {
      setTimeout(() => {
        const catalogItems = document.querySelectorAll('.catalog-item');
        if (index < catalogItems.length) {
          const item = catalogItems[index];
          const nameInput = item.querySelector('.catalog-name-input') as HTMLInputElement;
          const typeInput = item.querySelector('.catalog-type-input') as HTMLInputElement;
          const removedCheckbox = item.querySelector('.catalog-show-removed') as HTMLInputElement;

          const fields = [nameInput, typeInput, removedCheckbox].filter(Boolean);
          fields.forEach(field => field.setAttribute('disabled', 'true'));
          item.classList.add('catalog-disabled');
        }
      }, 0);
    }
  });
}

/**
 * Create a catalog item element
 */
function createCatalogItem(catalog: CatalogConfig, index: number, groupByType: boolean): HTMLElement {
  const div = document.createElement('div');
  div.className = 'catalog-item';
  div.setAttribute('data-catalog-id', catalog.id);
  div.setAttribute('draggable', 'true');

  // Type display label - special case for 'tv' type, otherwise capitalize first letter
  const typeLabel = catalog.type.toLowerCase() === 'tv'
    ? 'Live TV'
    : catalog.type.charAt(0).toUpperCase() + catalog.type.slice(1);

  // Type is editable when: groupByType is FALSE (single mixed catalog) OR it's Continue Watching
  const isTypeEditable = !groupByType || catalog.isContinueWatching;

  div.innerHTML = `
    <div class="catalog-header">
      <div class="drag-handle">⋮⋮</div>
      <div class="catalog-info">
        <!-- Name Row -->
        <div class="catalog-config-row">
          <label class="catalog-config-label">Name:</label>
          <input
            type="text"
            class="input catalog-name-input"
            value="${escapeHtml(catalog.name)}"
            placeholder="Catalog name"
            data-catalog-index="${index}"
          >
        </div>

        <!-- Type Row -->
        <div class="catalog-config-row">
          <label class="catalog-config-label">Type:</label>
          ${isTypeEditable ? `
            <input
              type="text"
              class="input catalog-type-input"
              value="${typeLabel}"
              placeholder="Type"
              data-catalog-index="${index}"
            >
            <span class="editable-label">Editable</span>
          ` : `
            <span class="catalog-type-badge">${typeLabel}</span>
          `}
        </div>

        ${!catalog.isContinueWatching ? `
          <!-- Sort Row -->
          <div class="catalog-config-row">
            <label class="catalog-config-label">Sort:</label>
            <select class="select select-compact" data-catalog-index="${index}" data-field="sort">
              ${Object.entries(ENABLED_SORT_OPTIONS)
                .map(
                  ([value, label]) =>
                    `<option value="${value}" ${
                      value === catalog.sortOrder ? 'selected' : ''
                    }>${label}</option>`
                )
                .join('')}
            </select>
          </div>

          <!-- Show Removed Row -->
          <div class="catalog-config-row">
            <label class="catalog-config-label">Show Removed:</label>
            <label class="toggle-switch">
              <input type="checkbox" class="catalog-show-removed" ${catalog.showRemovedItems ? 'checked' : ''} data-catalog-index="${index}">
              <span class="slider"></span>
            </label>
            <span class="tooltip" data-tip="Include items you've marked as removed from your library">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </span>
          </div>
        ` : ''}
      </div>
      <div class="catalog-actions">
        <label class="toggle-switch">
          <input type="checkbox" class="catalog-toggle-enabled" ${catalog.enabled ? 'checked' : ''} data-catalog-index="${index}">
          <span class="slider"></span>
        </label>
      </div>
    </div>
  `;

  // Add preview panel
  const previewPanel = document.createElement('div');
  previewPanel.className = 'catalog-preview';
  previewPanel.innerHTML = `
    <div class="preview-container">
      <!-- Loading state -->
      <div class="preview-loading" style="display: none;">
        ${Array(5).fill(0).map(() => `
          <div class="preview-skeleton">
            <div class="skeleton-poster"></div>
            <div class="skeleton-title"></div>
          </div>
        `).join('')}
      </div>

      <!-- Empty state -->
      <div class="preview-empty" style="display: none;">
        <span>Enable this catalog to preview</span>
      </div>

      <!-- Error state -->
      <div class="preview-error" style="display: none;">
        <span class="error-message"></span>
        <button class="button button-small button-secondary retry-button">Retry</button>
      </div>

      <!-- Loading overlay (for reloads) -->
      <div class="preview-loading-overlay" style="display: none;">
        <div class="spinner"></div>
      </div>

      <!-- Content -->
      <div class="preview-content" style="display: none;">
        <div class="preview-scroll">
          <!-- Items will be inserted here -->
        </div>
      </div>
    </div>
  `;
  div.appendChild(previewPanel);

  return div;
}

/**
 * Update catalog list when groupByType changes
 */
export function updateCatalogsByType(groupByType: boolean, config: Config): Config {
  debugConsole.log(`Updating catalogs: groupByType=${groupByType}`);

  // Preserve Continue Watching catalog
  const continueWatching = config.catalogs.find(c => c.isContinueWatching);

  if (groupByType) {
    // Create separate catalogs for each type
    const regularCatalogs = CONTENT_TYPES.map((type, index) => {
      // Map type to catalog ID enum
      let catalogIdEnum: number;
      switch (type) {
        case 'movie':
          catalogIdEnum = CatalogIdEnum.LIBRARY_MOVIE;
          break;
        case 'series':
          catalogIdEnum = CatalogIdEnum.LIBRARY_SERIES;
          break;
        case 'channel':
          catalogIdEnum = CatalogIdEnum.LIBRARY_CHANNEL;
          break;
        case 'tv':
          catalogIdEnum = CatalogIdEnum.LIBRARY_TV;
          break;
        default:
          catalogIdEnum = CatalogIdEnum.LIBRARY_MIXED;
      }

      return {
        id: CatalogIDStrings[catalogIdEnum as CatalogId],
        name: 'Library',
        type,
        sortOrder: 'lastWatched' as const,
        enabled: true,
        position: index + 1,
        showRemovedItems: false,
      };
    });

    config.catalogs = continueWatching ? [continueWatching, ...regularCatalogs] : regularCatalogs;
  } else {
    // Single mixed catalog
    const regularCatalog = {
      id: CatalogIDStrings[CatalogIdEnum.LIBRARY_MIXED],
      name: 'Library',
      type: 'mixed' as const,
      sortOrder: 'lastWatched' as const,
      enabled: true,
      position: 1,
      showRemovedItems: false,
    };

    config.catalogs = continueWatching ? [continueWatching, regularCatalog] : [regularCatalog];
  }

  return config;
}

/**
 * Setup collapsible sections
 */
export function setupCollapsibleSections(): void {
  const cacheHeader = document.getElementById('cacheHeader');
  const cacheContent = document.getElementById('cacheContent');
  const advancedHeader = document.getElementById('advancedHeader');
  const advancedContent = document.getElementById('advancedContent');

  if (cacheHeader && cacheContent) {
    cacheHeader.addEventListener('click', () => {
      toggleSection('cacheHeader', 'cacheContent');
    });
  }

  if (advancedHeader && advancedContent) {
    advancedHeader.addEventListener('click', () => {
      toggleSection('advancedHeader', 'advancedContent');
    });
  }
}

/**
 * Setup drag and drop for catalog reordering
 * Uses CatalogDragDropManager for desktop and touch support
 */
export function setupDragAndDrop(onOrderChange: (newOrder: string[]) => void): void {
  const catalogList = document.getElementById('catalogList');
  if (!catalogList) return;

  // Destroy existing manager if any
  if (dragDropManager) {
    dragDropManager.destroy();
  }

  // Create new manager
  dragDropManager = new CatalogDragDropManager(catalogList, onOrderChange);
  debugConsole.log('[UI] Drag-and-drop manager initialized');
}

/**
 * Cleanup drag-and-drop manager
 */
export function cleanupDragAndDrop(): void {
  if (dragDropManager) {
    dragDropManager.destroy();
    dragDropManager = null;
    debugConsole.log('[UI] Drag-and-drop manager cleaned up');
  }
}

/**
 * Escape HTML to prevent XSS
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Animate element
 */
export function animateElement(element: HTMLElement, animationClass: string): void {
  element.classList.add(animationClass);
  element.addEventListener(
    'animationend',
    () => {
      element.classList.remove(animationClass);
    },
    { once: true }
  );
}

/**
 * Clear all preview instances
 */
export function clearAllPreviews(): void {
  debugConsole.log('[UI] Clearing all preview instances');
  for (const preview of catalogPreviews.values()) {
    preview.destroy();
  }
  catalogPreviews.clear();
}

/**
 * Initialize preview for a catalog
 */
export function initializeCatalogPreview(
  catalogId: string,
  catalogType: string,
  configHash: string,
  enabled: boolean
): void {
  const catalogItem = document.querySelector(`[data-catalog-id="${catalogId}"]`);
  if (!catalogItem) {
    debugConsole.error(`[UI] Cannot find catalog item for ${catalogId}`);
    return;
  }

  const previewContainer = catalogItem.querySelector('.catalog-preview') as HTMLElement;
  if (!previewContainer) {
    debugConsole.error(`[UI] Cannot find preview container for ${catalogId}`);
    return;
  }

  // Destroy existing preview if any
  const existingPreview = catalogPreviews.get(catalogId);
  if (existingPreview) {
    existingPreview.destroy();
  }

  // Create new preview instance
  const preview = new CatalogPreview(
    previewContainer,
    configHash,
    catalogType,
    catalogId
  );

  catalogPreviews.set(catalogId, preview);

  // Load preview if catalog is enabled
  if (enabled) {
    debugConsole.log(`[UI] Loading preview for enabled catalog: ${catalogId}`);
    preview.loadPreview();
  } else {
    debugConsole.log(`[UI] Catalog ${catalogId} is disabled, showing empty state`);
  }
}

/**
 * Get preview instance for a catalog
 */
export function getCatalogPreview(catalogId: string): CatalogPreview | undefined {
  return catalogPreviews.get(catalogId);
}
